import { fbt } from 'fbtee'
import { APP_NAME } from '@shared/brand'
import type { FeedbackCategory } from '@/types'

// Shared, reusable translated strings. When the same user-facing string is used
// in the same context across more than one place, define it here once so the
// wording — and its single fbt translation key — stays consistent. Strings that
// are genuinely context-specific (a tooltip vs. a menu item vs. a heading) should
// stay inline so translators can render them differently per context.

// Browser tab title shown while a route page is still loading its data.
// Used by every page that passes a loading fallback to useDocumentTitle.
export function loadingDocumentTitle(): string {
  return `${fbt('Loading…', 'Browser tab title shown while a page is loading')} · ${APP_NAME}`
}

// Fallback display name for a group member whose profile has no display name.
// Description reuses the existing key so the translation carries over.
export function unknownMemberName(): string {
  return fbt('Unknown member', 'Fallback name shown when a group member has no display name')
}

// The "You" pill beside the current user's own name in a list of people.
// Description reuses the existing key so the translation carries over.
export function youPillLabel(): string {
  return fbt('You', "Pill shown next to the current user's name in the group members list")
}

// "{n} members" / "1 member" — the count of people in a group.
// Description reuses the existing key so the translation carries over.
export function memberCountLabel(count: number): string {
  return fbt(
    fbt.plural('member', count, { many: 'members', showCount: 'yes' }),
    'Member count shown on the group detail page',
  )
}

// Generic retry button shown after a recoverable error.
export function retryButtonLabel(): string {
  return fbt('Try again', 'Button to retry after an error')
}

// Generic "go home" button shown on error screens.
export function goHomeButtonLabel(): string {
  return fbt('Go home', 'Button to navigate home after an error')
}

// "Back to home" button on the invalid-link screens (invite, friend invite, totem link).
// Description reuses the existing key so the translation carries over.
export function backToHomeButtonLabel(): string {
  return fbt('Back to home', 'Button to go back to the home page from the invalid invite page')
}

// Not-found state for any page keyed by an event id.
export function eventNotFoundMessage(): string {
  return fbt('Event not found.', 'Message when the requested event cannot be found')
}

// Generic cancel/dismiss button shown in dialogs and edit flows.
export function cancelButtonLabel(): string {
  return fbt('Cancel', 'Generic cancel button that dismisses a dialog or aborts an action')
}

// Generic save/confirm button shown in edit and upload flows.
export function saveButtonLabel(): string {
  return fbt('Save', 'Generic button that saves or confirms changes')
}

// Success toast after copying a group's invite link to the clipboard.
// Description reuses the existing key so the translation carries over.
export function inviteLinkCopiedToast(): string {
  return fbt('Invite link copied', 'Toast after the invite link is copied to clipboard')
}

// Error toast when joining a group from a link (invite code or shared schedule).
// Description reuses the existing key so the translation carries over.
export function joinGroupFailedToast(): string {
  return fbt("Couldn't join group", 'Toast error when joining a group via invite link fails')
}

// Error toast when copying a group's invite link fails.
// Description reuses the existing key so the translation carries over.
export function inviteLinkCopyErrorToast(): string {
  return fbt("Couldn't copy invite link", 'Toast error when copying the invite link fails')
}

export function eventLinkCopiedToast(): string {
  return fbt('Event link copied', 'Toast after the event link is copied to clipboard')
}

export function eventLinkCopyErrorToast(): string {
  return fbt("Couldn't copy event link", 'Toast error when copying the event link fails')
}

// Error toast when copying any other value (a token, a calendar URL) fails.
export function copyFailedToast(): string {
  return fbt("Couldn't copy", 'Toast error when copying a value to the clipboard fails')
}

// Fallback display name for users without a profile name.
export function beaconUserFallback(): string {
  return fbt('Faro user', 'Fallback name for a user with no display name')
}

// Fallback name for a friend whose profile hasn't loaded yet.
export function friendFallbackName(): string {
  return fbt('A friend', 'Fallback name for a friend whose profile is loading')
}

// --- Block / unblock actions ---

// Menu item label for blocking a user.
export function blockUserLabel(): string {
  return fbt('Block user', 'Menu item to block a user')
}

// Confirm dialog title for blocking a user.
export function blockUserConfirmTitle(): string {
  return fbt('Block this user?', 'Confirm dialog title for blocking a user')
}

// Confirm button label to block a user.
export function blockConfirmLabel(): string {
  return fbt('Block', 'Confirm button to block a user')
}

// Toast shown after unblocking a user.
export function userUnblockedToast(): string {
  return fbt('User unblocked', 'Toast after unblocking a user')
}

// --- Admin tool labels ---

// Admin nav label for the artist images moderation tool.
export function adminArtistImagesLabel(): string {
  return fbt('Artist images', 'Title for the admin tool that moderates pending artist images')
}

// Admin nav label for the events editor tool.
export function adminEventsLabel(): string {
  return fbt('Events', 'Title for the admin tool that edits event metadata')
}

// Admin nav label for the queue of draft events awaiting review.
export function adminPendingEventsLabel(): string {
  return fbt('Pending events', 'Title for the admin tool that reviews draft events')
}

// Admin nav label for the queue of user-submitted event suggestions.
export function adminEventSuggestionsLabel(): string {
  return fbt('Event suggestions', 'Title for the admin tool that lists user-submitted event suggestions')
}

// Admin nav label for the queue of user-reported set-time publications.
export function adminScheduleLeadsLabel(): string {
  return fbt('Schedule leads', 'Title for the admin tool that reviews user-reported set times')
}

export function adminSetReportsLabel(): string {
  return fbt('Set reports', 'Title for the admin tool that reviews problems users reported on a set')
}

export function adminEventReportsLabel(): string {
  return fbt('Event reports', "Title for the admin tool that reviews problems users reported on an event's listing")
}

// Shared by the set and event "What's wrong?" dialogs; descriptions kept from
// when only sets had one, so their translations carry over.
export function reportSentToast(): string {
  return fbt("Thanks — we'll check it against the source.", 'Toast after reporting a problem with a set')
}

export function reportSendFailedToast(): string {
  return fbt('Could not send your report', 'Error toast when reporting a problem with a set fails')
}

export function reportOtherNotePlaceholder(): string {
  return fbt("Tell us what's wrong", 'Placeholder for the required note on an "other" set report')
}

export function adminSetTimeWatchlistLabel(): string {
  return fbt('Set-time watchlist', 'Title for the admin tool that tracks upcoming festivals awaiting set times')
}

// Admin: festival apps found without an extractor, grouped for engineering (watchlist link + page title).
export function adminProviderBacklogLabel(): string {
  return fbt('App provider backlog', 'Title for the admin list of festival apps found that Faro cannot read yet, grouped by provider')
}

export function adminDiscoveryMetricsLabel(): string {
  return fbt('Discovery metrics', 'Title for the admin page charting festival source discovery coverage, queue, runs and daily quotas')
}

// Admin nav label for the scheduled cloud routines' run reports.
export function adminRoutineRunsLabel(): string {
  return fbt('Routine runs', 'Title for the admin page listing scheduled routine run reports')
}

// Title of the routine metrics page and the link to it from the routine runs page.
export function adminRoutineMetricsLabel(): string {
  return fbt('Routine metrics', 'Title for the admin page charting scheduled routine spend and outcomes')
}

// Admin nav label for the queue of user-requested areas.
export function adminRegionRequestsLabel(): string {
  return fbt('Area requests', 'Title for the admin tool that reviews user-requested regions')
}

// Admin nav label for the user feedback review queue.
export function adminFeedbackLabel(): string {
  return fbt('Feedback', 'Title for the admin tool that reviews user-submitted feedback')
}

// Admin nav label for the metrics dashboard.
export function adminMetricsLabel(): string {
  return fbt('Metrics', 'Title for the admin metrics dashboard tool')
}

// Placeholder for the event search box in admin tools (events list, metrics picker).
// Description reuses the existing key so the translation carries over.
export function adminSearchEventsPlaceholder(): string {
  return fbt('Search events…', 'Placeholder for search bar on admin events page')
}

// Empty state when an admin event search turns up nothing.
// Description reuses the existing key so the translation carries over.
export function adminNoMatchingEventsLabel(): string {
  return fbt('No matching events.', 'Empty state when admin event search/filter has no results')
}

// Loading label shown by admin pages while their data loads.
// Description reuses the existing key so the translation carries over.
export function adminLoadingLabel(): string {
  return fbt('Loading…', 'Loading state on the admin feedback page')
}

// Admin nav label for entering festival mode.
export function adminFestivalModeLabel(): string {
  return fbt('Festival mode', 'Title for the admin tool that enters festival mode')
}

// Admin nav label for the clock override tool.
export function adminClockOverrideLabel(): string {
  return fbt('Clock override', 'Title for the admin clock override tool')
}

// --- Feedback / support form categories ---

// Category label for bug reports in feedback and support forms.
export function feedbackBugReportCategory(): string {
  return fbt('Bug report', 'Feedback form category for bug reports')
}

// Category label for feature requests in feedback and support forms.
export function feedbackFeatureRequestCategory(): string {
  return fbt('Feature request', 'Feedback form category for feature requests')
}

export function feedbackGeneralFeedbackCategory(): string {
  return fbt('General feedback', 'Feedback form category for general opinions or reactions')
}

// Category label for help/question in feedback and support forms.
export function feedbackHelpQuestionCategory(): string {
  return fbt('Help / Question', 'Feedback form category for support questions')
}

// Category label for "Other" in feedback and support forms.
export function feedbackOtherCategory(): string {
  return fbt('Other', 'Feedback form category for anything else')
}

// Display label for a stored feedback category — used by the admin queue and
// the submitter's own My requests row.
export function categoryLabel(category: FeedbackCategory): string {
  switch (category) {
    case 'bug':
      return feedbackBugReportCategory()
    case 'feature':
      return feedbackFeatureRequestCategory()
    case 'feedback':
      return feedbackGeneralFeedbackCategory()
    case 'support':
      return feedbackHelpQuestionCategory()
    default:
      return feedbackOtherCategory()
  }
}

// Submit button label for feedback and support forms.
export function feedbackSendLabel(): string {
  return fbt('Send', 'Button label to submit the feedback form')
}

// --- Search / group actions ---

// Accessible label for the clear-search button in search inputs.
export function clearSearchLabel(): string {
  return fbt('Clear search', 'Accessible label for the clear-search button')
}

// Accessible label for the create group button.
export function createGroupLabel(): string {
  return fbt('Create group', 'Accessible label for the create group button')
}

// Section heading for events a user is going to.
export function goingSectionHeading(): string {
  return fbt('Going', 'Section heading for events a user is going to on their profile')
}

// "Crew" — the groups-and-friends surface. One key shared by the app nav tab,
// festival mode's nav tab, and the event page's pill nav so the three never drift.
export function crewTabLabel(): string {
  return fbt('Crew', 'Nav label for the crew tab showing groups and friends')
}

// The festival-mode "add people to your crew" action. Shared by the Crew tab's
// header button (as its accessible label) and its empty-state CTA, which sit on
// the same screen — two wordings there would read as two different features.
export function addPeopleLabel(): string {
  return fbt('Add people', 'Label for the action that adds people to your crew in festival mode')
}

// Shown after a password reset email goes out — from the login form's "Forgot
// password?" and from Settings > Change password.
export function passwordResetSentMessage(): string {
  return fbt(
    'Password reset email sent. Check your inbox.',
    'Confirmation shown after a password reset email is sent',
  )
}

/** Suggest-an-event CTA on Discover (All) and the Search tab's results footer. */
export function suggestEventLabel(): string {
  return fbt('Suggest an event', 'Button label to suggest a festival or party be added')
}

// Bridge label shown next to the Faro name for a year after the rename (#585),
// so people who knew the app as Beacon recognize it.
export function formerlyBeaconLabel(): string {
  return fbt('formerly Beacon', 'Small label under the app name saying the app used to be called Beacon')
}

export const friendInviteQrTitle = () => fbt('Add me on Faro', 'Title of the friend invite QR code dialog')

export const friendInviteQrDescription = () =>
  fbt('Scan this code or share the link to add me as a friend.', 'Instructions in the friend invite QR code dialog')

// The guest sign-up dialog's accept button and the guest picks footer's CTA.
export function logInButtonLabel(): string {
  return fbt('Log in', 'Button to navigate to the login page from the upsell dialog')
}

// Note on a festival map that is still the previous edition's (Event.mapImageFromEventId),
// shown under the event page's map and on festival mode's map.
export function provisionalMapLabel(): string {
  return fbt(
    "Last edition's map — this year's isn't available yet",
    "Note on a festival map that is still the previous edition's, until this year's map is published",
  )
}
